/**
 * +field: quest tracker rows (right side, where the M+ timer sits in a key): the zone's current
 * intro-chain step, then every accepted bounty with its progress. Pure.
 */
import type { DungeonDef } from '@keystone/sim';
import type { ChainStep, HeroCharacter } from '@keystone/progression';
import { boardUnlocked, bountyDef, currentChainStep, zoneQuests } from '@keystone/progression';
import type { TrackerRow, Waypoint } from './fieldView';

export interface TrackerNames {
  npc(id: string): string | undefined;
  map(id: string): string | undefined;
  /** +zone: an area's name on the current zone map. */
  area?(id: string): string | undefined;
}

/** "Meadow Slimes or Bloom Slimes" from mob ids. */
function mobList(ids: readonly string[], names: TrackerNames): string {
  const n = ids.map((id) => names.npc(id) ?? id);
  return n.length <= 1 ? (n[0] ?? '') : `${n.slice(0, -1).join(', ')} or ${n[n.length - 1]}`;
}

/** The objective line of a main-quest step. */
export function stepText(step: ChainStep, ch: HeroCharacter, names: TrackerNames): string {
  switch (step.kind) {
    case 'talk':
      return `Speak with ${names.npc(step.npcId) ?? step.npcId}${step.mapId !== ch.mapId ? ` in ${names.map(step.mapId) ?? step.mapId}` : ''}`;
    case 'goto':
      return `Travel to ${(step.areaId && names.area?.(step.areaId)) || (names.map(step.mapId) ?? step.mapId)}`;
    case 'kill':
      return `Slay ${mobList(step.npcIds, names)}`;
    case 'enter':
      return `Find the entrance to ${names.map(step.dungeonId) ?? step.dungeonId}`;
    case 'clear':
      return `Clear ${names.map(step.dungeonId) ?? step.dungeonId}`;
    case 'auto':
      return 'Press Z: let Auto defeat enemies';
    case 'level':
      return `Reach level ${step.level}`;
  }
}

export function trackerRows(ch: HeroCharacter | null, names: TrackerNames, heroLevel = 1): TrackerRow[] {
  if (!ch) return [];
  const rows: TrackerRow[] = [];
  const z = zoneQuests(ch.zoneId);
  const step = currentChainStep(ch.bounties, ch.zoneId);
  if (step) {
    const counted = step.kind === 'kill' || step.kind === 'auto';
    const cur = counted ? (ch.bounties.chainProgress[ch.zoneId] ?? 0) : step.kind === 'level' ? heroLevel : 0;
    const max = counted ? step.count : step.kind === 'level' ? step.level : 1;
    rows.push({ id: step.id, kind: 'chain', title: step.title, text: stepText(step, ch, names), cur: Math.min(cur, max), max, done: false, main: true });
  }
  for (const id of Object.keys(ch.bounties.active).sort()) {
    const def = bountyDef(id);
    if (!def) continue;
    const cur = Math.min(def.count, ch.bounties.active[id] ?? 0);
    const done = cur >= def.count;
    rows.push({
      id,
      kind: 'bounty',
      title: def.title,
      text: `Slay ${mobList(def.npcIds, names)}${def.areaId && names.area?.(def.areaId) ? ` in ${names.area(def.areaId)}` : ''}`,
      cur,
      max: def.count,
      done,
      ...(done ? { hint: `Turn in at the ${names.npc(def.boardNpcId ?? z?.boardNpcId ?? '') ?? 'Bounty Board'}${def.boardAreaId && names.area?.(def.boardAreaId) ? ` (${names.area(def.boardAreaId)})` : ''}` } : {}),
    });
  }
  return rows;
}

/**
 * Where the tracker arrow points on `mapId` for the main quest's current step: the step's waypoint
 * when it is on this map, else the portal of the shortest portal route toward the waypoint's map.
 * Null inside a dungeon, for steps without a waypoint, or when no route exists.
 */
export function waypointOn(ch: HeroCharacter | null, mapId: string, maps: readonly DungeonDef[]): Waypoint | null {
  if (!ch) return null;
  const step = currentChainStep(ch.bounties, ch.zoneId);
  const wp = step?.waypoint;
  if (!step || !wp) return null;
  const here = maps.find((d) => d.id === mapId);
  if (!here?.field) return null;
  if (wp.mapId === mapId) return { pos: { ...wp.pos }, label: step.title, portal: false };
  // Breadth-first over the open maps' portals (dungeon portals lead nowhere walkable).
  const prev = new Map<string, { from: string; portal: string } | null>([[mapId, null]]);
  const queue = [mapId];
  while (queue.length) {
    const cur = queue.shift()!;
    if (cur === wp.mapId) break;
    for (const p of maps.find((d) => d.id === cur)?.field?.portals ?? []) {
      if (p.kind === 'dungeon' || prev.has(p.to) || !maps.find((d) => d.id === p.to)?.field) continue;
      prev.set(p.to, { from: cur, portal: p.id });
      queue.push(p.to);
    }
  }
  if (!prev.has(wp.mapId)) return null;
  let at = wp.mapId;
  let hop = prev.get(at)!;
  while (hop && hop.from !== mapId) {
    at = hop.from;
    hop = prev.get(at)!;
  }
  const portal = hop ? here.field.portals.find((p) => p.id === hop!.portal) : undefined;
  return portal ? { pos: { ...portal.pos }, label: portal.name, portal: true } : null;
}

/** Quest marker over each town NPC: '!' a talk step waits there, '?' a bounty is ready to turn in, else available bounties on the board. */
export function npcMarkers(ch: HeroCharacter | null, level: number): Record<string, 'available' | 'progress' | 'turnin' | null> {
  const out: Record<string, 'available' | 'progress' | 'turnin' | null> = {};
  if (!ch) return out;
  const z = zoneQuests(ch.zoneId);
  if (!z) return out;
  const step = currentChainStep(ch.bounties, ch.zoneId);
  // +zone: the board stays dark until the main quest sends you to it.
  if (boardUnlocked(ch.bounties, ch.zoneId)) {
    // +zone: each board (the town's, the Lakeside Outpost's) marks its own bounties.
    const boards = new Set(z.bounties.map((b) => b.boardNpcId ?? z.boardNpcId));
    for (const board of boards) {
      const mine = z.bounties.filter((b) => (b.boardNpcId ?? z.boardNpcId) === board);
      const active = mine.filter((b) => ch.bounties.active[b.id] !== undefined);
      const ready = active.some((b) => (ch.bounties.active[b.id] ?? 0) >= (bountyDef(b.id)?.count ?? Infinity));
      const offer = mine.some((b) => ch.bounties.active[b.id] === undefined && (b.minLevel ?? 1) <= level);
      const mark = ready ? 'turnin' : offer ? 'available' : active.length ? 'progress' : null;
      if (mark || board === z.boardNpcId) out[board] = mark;
    }
  }
  if (step?.kind === 'talk') out[step.npcId] = 'available';
  return out;
}
